import { LinearGradient } from "expo-linear-gradient";
import { Box, Image, Pressable, Text, useTheme, VStack } from "native-base";
import React from "react";

import { getSimpleRecipeImage } from "../helpers/generalHelpers";
import type { SimpleRecipe, SimpleRecipeMeal } from "../services/backendTypes";

interface CategoryCardProps {
  name: string;
  title: string;
  recipe: SimpleRecipe | null;
  onPress: () => void;
  index?: number;
}

// Helper function to convert SimpleRecipe to SimpleRecipeMeal format
const convertToSimpleRecipeMeal = (simpleRecipe: SimpleRecipe): SimpleRecipeMeal => ({
  id: simpleRecipe.id,
  simple_recipe: simpleRecipe,
  meal_servings: simpleRecipe.servings || 1,
  total_calories: simpleRecipe.total_calories,
  total_protein: simpleRecipe.protein_grams,
  total_carbohydrates: simpleRecipe.carbohydrates_grams,
  total_fat: simpleRecipe.fat_grams,
  total_fiber: simpleRecipe.fiber_grams || null,
});

const CategoryCard = ({ name, title, recipe, onPress, index = 0 }: CategoryCardProps): JSX.Element => {
  const theme = useTheme();
  const simpleRecipeMeal = recipe ? convertToSimpleRecipeMeal(recipe) : null;

  // Alternate between two background colors based on index (used as fallback when no recipe)
  const backgroundColor = index % 2 === 0 ? "primary.20" : "primary.30";

  return (
    <Pressable onPress={onPress} testID={`category-${name}`}>
      <Box
        w="172px"
        h="86px"
        overflow="hidden"
        position="relative"
        mr={3}
        borderRadius={theme.borderRadii.card.default}
        shadow={10}
        bg={recipe ? undefined : backgroundColor}
      >
        {/* Background Image - only if recipe is available */}
        {Boolean(recipe && simpleRecipeMeal) && (
          <>
            <Image
              source={getSimpleRecipeImage(simpleRecipeMeal!).source}
              alt={title}
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              width="100%"
              height="100%"
              resizeMode="cover"
            />

            {/* Gradient Overlay for Text Readability */}
            <LinearGradient
              colors={["transparent", `${theme.colors.function[50]}CC`, theme.colors.function[50]]}
              locations={[0.3, 0.7, 1]}
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: "100%",
              }}
            />
          </>
        )}

        {/* Text Content */}
        <VStack
          position="absolute"
          top={0}
          bottom={0}
          left={0}
          right={0}
          px={3}
          justifyContent="center"
          alignItems="center"
        >
          <Text
            fontSize="lg"
            fontFamily="body"
            fontWeight="700"
            color="function.10"
            numberOfLines={2}
            textAlign="center"
          >
            {title}
          </Text>
        </VStack>
      </Box>
    </Pressable>
  );
};

export default CategoryCard;
